import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/permissions";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Layers, ArrowRight, ChevronLeft } from "lucide-react";
import ConfirmDeleteDialog from "@/components/admin/ConfirmDeleteDialog";

export default function SubjectsPage() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [course, setCourse] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ subject_name: "", description: "", status: "active" });
  const [deleteDlg, setDeleteDlg] = useState({ open: false, id: null, label: "", busy: false });

  const load = () => {
    api.get(`/subjects?course_id=${courseId}`).then((r) => setItems(r.data));
    api.get("/courses").then((r) => setCourse(r.data.find((c) => c.id === courseId)));
  };
  useEffect(() => { load(); }, [courseId]);

  const reset = () => { setEditing(null); setForm({ subject_name: "", description: "", status: "active" }); };

  const save = async () => {
    try {
      const payload = { ...form, course_id: courseId };
      if (editing) { await api.patch(`/subjects/${editing.id}`, payload); toast.success("Updated"); }
      else { await api.post("/subjects", payload); toast.success("Created"); }
      setOpen(false); reset(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    setDeleteDlg((d) => ({ ...d, busy: true }));
    try {
      await api.delete(`/subjects/${id}`);
      toast.success("Subject deleted");
      setDeleteDlg({ open: false, id: null, label: "", busy: false });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete subject");
      setDeleteDlg((d) => ({ ...d, busy: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="subjects-page">
      <Link to="/admin/courses" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" />Back to Courses</Link>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">{course?.course_name || "Course"}</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Subjects</h1>
        </div>
        {hasPerm(user, "subject", "add") && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <DialogTrigger asChild><Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="add-subject-btn"><Plus className="w-4 h-4 mr-2" />New Subject</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Edit Subject" : "New Subject"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Subject name</Label><Input value={form.subject_name} onChange={(e) => setForm({ ...form, subject_name: e.target.value })} data-testid="subject-name-input" /></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div><Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={save} data-testid="subject-save-btn">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 ? <div className="text-slate-500 col-span-full">No subjects yet.</div> :
          items.map((s) => (
            <Card key={s.id} className="p-6 border-slate-200 hover:shadow-md transition-shadow" data-testid={`subject-${s.id}`}>
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-md bg-blue-50 text-blue-700 grid place-items-center"><Layers className="w-5 h-5" strokeWidth={1.5} /></div>
                <Badge className={s.status === "active" ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-slate-200"}>{s.status}</Badge>
              </div>
              <h3 className="mt-4 font-display text-lg font-bold">{s.subject_name}</h3>
              <p className="text-sm text-slate-600 mt-1 line-clamp-2">{s.description}</p>
              <div className="mt-5 flex items-center justify-between">
                <Link to={`/admin/subjects/${s.id}/modules`} className="text-sm font-semibold text-blue-700 inline-flex items-center gap-1 hover:underline" data-testid={`view-modules-${s.id}`}>
                  Modules <ArrowRight className="w-4 h-4" />
                </Link>
                <div className="flex gap-2">
                  {hasPerm(user, "subject", "edit") && <Button size="icon" variant="outline" onClick={() => { setEditing(s); setForm({ subject_name: s.subject_name, description: s.description || "", status: s.status }); setOpen(true); }}><Edit className="w-4 h-4" /></Button>}
                  {hasPerm(user, "subject", "delete") && <Button size="icon" variant="outline" className="text-red-600" onClick={() => setDeleteDlg({ open: true, id: s.id, label: s.subject_name, busy: false })} data-testid={`delete-subject-${s.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </div>
              </div>
            </Card>
          ))}
      </div>

      <ConfirmDeleteDialog
        open={deleteDlg.open}
        onCancel={() => setDeleteDlg({ open: false, id: null, label: "", busy: false })}
        onConfirm={() => del(deleteDlg.id)}
        title="Delete this subject?"
        itemLabel={deleteDlg.label}
        description="will be permanently removed along with ALL of its modules, questions, and AI explanations."
        busy={deleteDlg.busy}
        confirmLabel="Delete subject"
        dataTestId="delete-subject"
      />
    </div>
  );
}
